import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';
import './RelatedFeaturesFinder.css';

/**
 * RelatedFeaturesFinder - Découverte automatique de features liées
 * 
 * Utilise les embeddings + le graphe pour identifier automatiquement les features
 * liées à une feature donnée, avec analyse d'impact des changements.
 * 
 * Fonctionnalités :
 * - Recherche par sélection de nœud
 * - Similarité sémantique (embeddings)
 * - Connexions graphe (relations Neo4j)
 * - Badges d'impact (HIGH/MEDIUM/LOW)
 * - Explications des liens
 */
const RelatedFeaturesFinder = ({ preselectedNodeId = null }) => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [relatedFeatures, setRelatedFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [impactFilter, setImpactFilter] = useState('all'); // all, high, medium, low

  useEffect(() => {
    loadNodes();
  }, []);

  useEffect(() => {
    if (preselectedNodeId) {
      // Si un nœud est présélectionné, le charger automatiquement
      findRelatedFeatures(preselectedNodeId);
    }
  }, [preselectedNodeId]);

  const loadNodes = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/smart-tools/nodes');
      setNodes(data.nodes || []);
    } catch (error) {
      console.error('Error loading nodes:', error);
      toast.error(t('smartTools.relatedFinder.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const findRelatedFeatures = async (nodeId) => {
    if (!nodeId) return;

    setSearching(true);
    setSelectedNode(nodeId);

    try {
      const { data } = await apiClient.get(`/api/smart-tools/find-related/${nodeId}`);
      setRelatedFeatures(data.related || []);
      
      if (data.related.length === 0) {
        toast.info(t('smartTools.relatedFinder.noRelated'));
      } else {
        toast.success(t('smartTools.relatedFinder.foundRelated', { count: data.related.length }));
      }
    } catch (error) {
      console.error('Error finding related features:', error);
      toast.error(t('smartTools.relatedFinder.searchError'));
    } finally {
      setSearching(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getImpactLabel = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return t('smartTools.relatedFinder.highImpact');
      case 'medium': return t('smartTools.relatedFinder.mediumImpact');
      case 'low': return t('smartTools.relatedFinder.lowImpact');
      default: return t('smartTools.relatedFinder.unknownImpact');
    }
  };

  const filteredFeatures = relatedFeatures.filter(feature => {
    if (impactFilter === 'all') return true;
    return feature.impact?.toLowerCase() === impactFilter;
  });

  return (
    <div className="related-features-finder">
      <div className="finder-header">
        <div className="header-content">
          <h2>🔗 {t('smartTools.relatedFinder.title')}</h2>
          <p className="header-description">{t('smartTools.relatedFinder.description')}</p>
        </div>
      </div>

      <div className="search-section">
        <div className="search-control">
          <label>{t('smartTools.relatedFinder.selectNode')}</label>
          <select
            value={selectedNode || ''}
            onChange={(e) => findRelatedFeatures(e.target.value)}
            disabled={loading || searching}
            className="node-selector"
          >
            <option value="">{t('smartTools.relatedFinder.chooseNode')}</option>
            {nodes.map((node) => (
              <option key={node.id} value={node.id}>
                {node.name} ({node.type})
              </option>
            ))}
          </select>
        </div>

        {searching && (
          <div className="searching-indicator">
            <div className="spinner-small"></div>
            <span>{t('smartTools.relatedFinder.searching')}</span>
          </div>
        )}
      </div>

      {selectedNode && relatedFeatures.length > 0 && (
        <>
          <div className="filter-section">
            <label>{t('smartTools.relatedFinder.filterByImpact')}</label>
            <div className="impact-filters">
              <button
                onClick={() => setImpactFilter('all')}
                className={`filter-button ${impactFilter === 'all' ? 'active' : ''}`}
              >
                {t('smartTools.relatedFinder.all')} ({relatedFeatures.length})
              </button>
              <button
                onClick={() => setImpactFilter('high')}
                className={`filter-button ${impactFilter === 'high' ? 'active' : ''}`}
                style={{ borderColor: '#dc2626' }}
              >
                🔴 {t('smartTools.relatedFinder.high')} ({relatedFeatures.filter(f => f.impact?.toLowerCase() === 'high').length})
              </button>
              <button
                onClick={() => setImpactFilter('medium')}
                className={`filter-button ${impactFilter === 'medium' ? 'active' : ''}`}
                style={{ borderColor: '#f59e0b' }}
              >
                🟡 {t('smartTools.relatedFinder.medium')} ({relatedFeatures.filter(f => f.impact?.toLowerCase() === 'medium').length})
              </button>
              <button
                onClick={() => setImpactFilter('low')}
                className={`filter-button ${impactFilter === 'low' ? 'active' : ''}`}
                style={{ borderColor: '#10b981' }}
              >
                🟢 {t('smartTools.relatedFinder.low')} ({relatedFeatures.filter(f => f.impact?.toLowerCase() === 'low').length})
              </button>
            </div>
          </div>

          <div className="features-list">
            <div className="list-header">
              <span>{t('smartTools.relatedFinder.showingResults', { count: filteredFeatures.length })}</span>
            </div>

            {filteredFeatures.map((feature) => (
              <div key={feature.node.id} className="feature-card">
                <div className="feature-header">
                  <div className="feature-info">
                    <span className="feature-type">{feature.node.type}</span>
                    <h4>{feature.node.name}</h4>
                  </div>
                  <div className="impact-badge-container">
                    <div
                      className="impact-badge"
                      style={{ backgroundColor: getImpactColor(feature.impact) }}
                    >
                      {getImpactLabel(feature.impact)}
                    </div>
                    <div className="similarity-score">
                      {(feature.similarity * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>

                <p className="feature-description">
                  {feature.node.description || t('smartTools.relatedFinder.noDescription')}
                </p>

                <div className="connection-info">
                  <div className="connection-badge">
                    {feature.directlyConnected ? (
                      <>
                        <span className="connection-icon">🔗</span>
                        {t('smartTools.relatedFinder.directConnection')}
                      </>
                    ) : (
                      <>
                        <span className="connection-icon">🌐</span>
                        {t('smartTools.relatedFinder.semanticConnection')}
                      </>
                    )}
                  </div>
                </div>

                <div className="explanation-section">
                  <h5>{t('smartTools.relatedFinder.whyRelated')}</h5>
                  <p className="explanation-text">{feature.explanation}</p>
                </div>

                <div className="feature-meta">
                  <span className="meta-item">🆔 ID: {feature.node.id}</span>
                  {feature.node.labels && (
                    <span className="meta-item">🏷️ {feature.node.labels.join(', ')}</span>
                  )}
                </div>
              </div>
            ))}

            {filteredFeatures.length === 0 && (
              <div className="no-results">
                <p>{t('smartTools.relatedFinder.noResultsForFilter')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {selectedNode && relatedFeatures.length === 0 && !searching && (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>{t('smartTools.relatedFinder.noRelatedFound')}</h3>
          <p>{t('smartTools.relatedFinder.noRelatedDescription')}</p>
        </div>
      )}

      {!selectedNode && (
        <div className="initial-state">
          <div className="initial-icon">🎯</div>
          <h3>{t('smartTools.relatedFinder.selectToStart')}</h3>
          <p>{t('smartTools.relatedFinder.selectDescription')}</p>
        </div>
      )}
    </div>
  );
};

export default RelatedFeaturesFinder;
